import {
  Dispatch,
  useEffect,
  useState,
  useRef,
  useCallback,
  SetStateAction
} from "react";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { getBraintreeCCClientToken } from "@tvg/api/paws/braintreeCC";
import { useIsomorphicLayoutEffect } from "@tvg/custom-hooks";
import { ErrorMessage } from "@tvg/wallet/src/types";
import { getAccountNumber } from "@urp/store-selectors";
import { getBraintreeCCToggle } from "../redux/toggles";
import {
  BRAINTREE_ERROR_CODES,
  BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS,
  BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS
} from "../constants";
import { createBraintreeClient } from "../utils/braintreeClient";
import type {
  BraintreeClientInstance,
  BraintreeDataCollectorInstance,
  BraintreeHostedFieldsInstance,
  HostedFieldsState,
  HostedFieldEvent,
  HostedFieldsCreateConfig
} from "../types/braintree";
import "../types/braintree";

const DEFAULT_FIELD_STATE = {
  container: null,
  isFocused: false,
  isEmpty: true,
  isPotentiallyValid: true,
  isValid: false
};

const DEFAULT_FIELDS_STATE: HostedFieldsState = {
  number: { ...DEFAULT_FIELD_STATE },
  expirationDate: { ...DEFAULT_FIELD_STATE },
  cvv: { ...DEFAULT_FIELD_STATE }
};

interface UseBraintreeHostedFieldsProps {
  isModalOpen: boolean;
  styles: Record<string, Record<string, string>>;
  setErrorMessage: Dispatch<SetStateAction<ErrorMessage>>;
  onFieldFocus?: (fieldName: string) => void;
  onFieldBlur?: (fieldName: string) => void;
}

export const fetchAndValidateClientToken = async (
  accountId: string,
  setErrorMessage: Dispatch<SetStateAction<ErrorMessage>>,
  signal?: AbortSignal
): Promise<{ token: string } | null> => {
  try {
    const response = await getBraintreeCCClientToken(accountId);
    const token = get(response, "data.token");

    if (!token || typeof token !== "string") {
      throw new Error("Invalid client token format");
    }

    return { token };
  } catch (err) {
    if (signal?.aborted) return null;

    const errorCode = String(
      get(err, "response.data.code") || BRAINTREE_ERROR_CODES.INIT_ERROR
    );

    setErrorMessage((prevState) => ({
      ...prevState,
      isOpen: true,
      errorCode,
      isRetry: false
    }));
  }

  return null;
};

export const useBraintreeHostedFields = ({
  isModalOpen,
  styles,
  setErrorMessage,
  onFieldFocus,
  onFieldBlur
}: UseBraintreeHostedFieldsProps) => {
  const accountId = useSelector(getAccountNumber);
  const isBraintreeCCEnabled = useSelector(getBraintreeCCToggle);

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldStates, setFieldStates] =
    useState<HostedFieldsState>(DEFAULT_FIELDS_STATE);

  const [deviceData, setDeviceData] = useState("");
  const [isSdkAvailable, setIsSdkAvailable] = useState(
    typeof window !== "undefined" &&
      !!window.braintree?.hostedFields &&
      !!window.braintree?.dataCollector
  );

  useEffect(() => {
    if (
      isSdkAvailable ||
      !isBraintreeCCEnabled ||
      !isModalOpen ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (window.braintree?.hostedFields && window.braintree?.dataCollector) {
        clearTimeout(timeoutId);
        setIsSdkAvailable(true);
        clearInterval(intervalId);
      }
    }, BRAINTREE_SDK_SCRIPT_POLL_INTERVAL_MS);

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      setErrorMessage((prevState) => ({
        ...prevState,
        isOpen: true,
        errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
        isRetry: false
      }));
    }, BRAINTREE_SDK_SCRIPT_LOAD_TIMEOUT_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isSdkAvailable, isBraintreeCCEnabled, isModalOpen, setErrorMessage]);

  const hostedFieldsInstanceRef = useRef<BraintreeHostedFieldsInstance | null>(
    null
  );
  const clientInstanceRef = useRef<BraintreeClientInstance | null>(null);
  const dataCollectorInstanceRef =
    useRef<BraintreeDataCollectorInstance | null>(null);
  const isInitializingRef = useRef(false);
  const teardownPromiseRef = useRef<Promise<void> | null>(null);
  const onFieldFocusRef = useRef(onFieldFocus);
  onFieldFocusRef.current = onFieldFocus;
  const onFieldBlurRef = useRef(onFieldBlur);
  onFieldBlurRef.current = onFieldBlur;

  const teardown = useCallback(() => {
    const promises: Promise<void>[] = [];

    if (hostedFieldsInstanceRef.current) {
      promises.push(hostedFieldsInstanceRef.current.teardown().catch(() => {}));
      hostedFieldsInstanceRef.current = null;
    }
    if (dataCollectorInstanceRef.current) {
      promises.push(
        dataCollectorInstanceRef.current.teardown().catch(() => {})
      );
      dataCollectorInstanceRef.current = null;
    }
    if (clientInstanceRef.current) {
      promises.push(clientInstanceRef.current.teardown().catch(() => {}));
      clientInstanceRef.current = null;
    }

    teardownPromiseRef.current =
      promises.length > 0 ? Promise.all(promises).then(() => {}) : null;

    setIsReady(false);
    setIsLoading(false);
    setDeviceData("");
    setFieldStates(DEFAULT_FIELDS_STATE);
    isInitializingRef.current = false;
  }, []);

  const tokenize = useCallback(async (): Promise<{
    nonce: string;
    details: { cardType: string; lastFour: string };
  }> => {
    if (!hostedFieldsInstanceRef.current) {
      throw new Error("Hosted Fields not initialized");
    }

    const payload = await hostedFieldsInstanceRef.current.tokenize();
    return payload;
  }, []);

  useEffect(() => {
    if (!isModalOpen || !isBraintreeCCEnabled) {
      teardown();
      isInitializingRef.current = false;
    }
  }, [isModalOpen, isBraintreeCCEnabled, teardown]);

  useEffect(() => () => teardown(), [teardown]);

  useIsomorphicLayoutEffect(() => {
    if (
      !isBraintreeCCEnabled ||
      !isModalOpen ||
      !accountId ||
      isReady ||
      isInitializingRef.current ||
      typeof window === "undefined" ||
      !window.braintree?.hostedFields ||
      !window.braintree?.dataCollector
    ) {
      return undefined;
    }

    const abortController = new AbortController();

    const initializeHostedFields = async () => {
      isInitializingRef.current = true;
      setIsLoading(true);

      try {
        if (teardownPromiseRef.current) {
          await teardownPromiseRef.current;
          teardownPromiseRef.current = null;
        }
        if (abortController.signal.aborted) return;

        const tokenData = await fetchAndValidateClientToken(
          accountId,
          setErrorMessage,
          abortController.signal
        );
        if (!tokenData || abortController.signal.aborted) {
          return;
        }

        const clientInstance = await createBraintreeClient(tokenData.token);
        if (abortController.signal.aborted) {
          clientInstance.teardown().catch(() => {});
          return;
        }
        clientInstanceRef.current = clientInstance;

        const hostedFields = window.braintree!.hostedFields!;

        const fieldsConfig: HostedFieldsCreateConfig = {
          client: clientInstance,
          styles,
          fields: {
            number: {
              container: "#braintree-card-number",
              placeholder: "0000 0000 0000 0000"
            },
            expirationDate: {
              container: "#braintree-expiration-date",
              placeholder: "MM/YY"
            },
            cvv: {
              container: "#braintree-cvv",
              placeholder: "3 digits"
            }
          }
        };

        const hostedFieldsInstance = await hostedFields.create(fieldsConfig);
        if (abortController.signal.aborted) {
          hostedFieldsInstance.teardown().catch(() => {});
          clientInstance.teardown().catch(() => {});
          clientInstanceRef.current = null;
          return;
        }
        hostedFieldsInstanceRef.current = hostedFieldsInstance;

        hostedFieldsInstance.on("validityChange", (event: HostedFieldEvent) => {
          setFieldStates({ ...event.fields });
        });

        hostedFieldsInstance.on("empty", (event: HostedFieldEvent) => {
          setFieldStates({ ...event.fields });
        });

        hostedFieldsInstance.on("notEmpty", (event: HostedFieldEvent) => {
          setFieldStates({ ...event.fields });
        });

        hostedFieldsInstance.on("focus", (event: HostedFieldEvent) => {
          setFieldStates({ ...event.fields });
          onFieldFocusRef.current?.(event.emittedBy);
        });

        hostedFieldsInstance.on("blur", (event: HostedFieldEvent) => {
          setFieldStates({ ...event.fields });
          onFieldBlurRef.current?.(event.emittedBy);
        });

        if (!window.braintree?.dataCollector) {
          throw new Error("Braintree Data Collector SDK not loaded");
        }

        const dataCollectorInstance =
          await window.braintree.dataCollector.create({
            client: clientInstance
          });
        if (abortController.signal.aborted) {
          dataCollectorInstance.teardown().catch(() => {});
          return;
        }
        dataCollectorInstanceRef.current = dataCollectorInstance;
        setDeviceData(dataCollectorInstance.deviceData);

        setErrorMessage((prevState) => ({
          ...prevState,
          isOpen: false,
          errorCode: ""
        }));
        setIsLoading(false);
        setIsReady(true);
      } catch (err: unknown) {
        if (abortController.signal.aborted) return;
        teardown();
        setErrorMessage((prevState) => ({
          ...prevState,
          isOpen: true,
          errorCode: BRAINTREE_ERROR_CODES.INIT_ERROR,
          isRetry: false
        }));
      } finally {
        isInitializingRef.current = false;
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    initializeHostedFields();

    return () => {
      abortController.abort();
    };
  }, [
    isBraintreeCCEnabled,
    isModalOpen,
    isReady,
    isSdkAvailable,
    accountId,
    styles,
    setErrorMessage
  ]);

  return {
    isReady,
    isLoading,
    fieldStates,
    deviceData,
    tokenize,
    teardown
  };
};
